import { Form } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import {
  type ChangeEvent,
  type FocusEvent,
  type ReactNode,
  type RefObject,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
} from "@/components/responsive-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useOnline } from "@/hooks/use-online"
import { agencyInvitationsPath } from "@/routes"

interface NewBrandDialogProps {
  /**
   * The agency the brand will be auto-attached to. Surfaced inline as
   * an attribution hint above the form fields ("This brand will be
   * auto-attached to {{agency}}.") so the agency admin understands the
   * implicit ownership before they submit.
   *
   * Backend derives the actual `agency_id` server-side from
   * `Current.user.agency_id` per OKS-13 BE2 — there is NO `agency_id`
   * field on the wire. Mismatching `agencyName` here vs the
   * server-derived agency would only mislead the user; it cannot create
   * a wrong-agency record.
   */
  agencyName: string
  /** Trigger element passed via `<DialogTrigger asChild>`. */
  trigger: ReactNode
  /**
   * Optional ref forwarded for explicit focus return on close. Standard
   * shadcn flow already covers the trigger-on-page case.
   */
  triggerRef?: RefObject<HTMLElement | null>
}

/**
 * OKS-13 Path B — Agency creates a brand under itself + first-admin invite.
 *
 * The agency-side analogue of OKS-12's `NewTenantDialog` (kind="brand").
 * Same three fields (`name`, `locale`, `admin_email`) but POSTs to
 * `agencyInvitationsPath()` with hidden `path: "brand"`. Backend's
 * `AgencyInvitationsController` derives `agency_id` from
 * `Current.user.agency_id` — there is no `agency_id` field on the wire.
 *
 * Atomic per BE TRD: backend wraps brand create + auto-attach (Brand→
 * Agency) + invitation create + mailer-deliver-later in a single
 * transaction. Field-level errors render via the Inertia render-prop
 * `errors`; transactional failures land in `errors.base` and surface in
 * the top banner.
 *
 * Crew form rules: Inertia `<Form>` (NOT shadcn Form); plain `Input` +
 * `Label` + `<InputError>` + Radix `<Select>` with `name`-via-hidden-
 * input for form submission; no react-hook-form; no zod.
 *
 * Locked QA testid (OKS-13 v4 freeze):
 *   - `agency-new-brand-trigger` on the trigger element (consumer Button)
 *   - `agency-new-brand-dialog` on the DialogContent
 *   - `agency-new-brand-submit` on the primary button
 *   - `agency-new-brand-locale` on the SelectTrigger
 *   - `agency-new-brand-error-banner` / `agency-new-brand-offline-banner`
 *
 * Why a separate component vs. extending OKS-12's `NewTenantDialog`:
 * three things differ — endpoint URL (`agencyInvitationsPath` vs
 * `adminInvitationsPath`), i18n namespace (`agency.brands.dialog.*` vs
 * `admin.invites.dialog.*`), and the attribution-hint slot. Threading
 * three optional props through the OKS-12 component for one extra
 * caller is more surface area than this clone, which carries
 * exactly-the-same form state machinery 1:1.
 */
export function NewBrandDialog({
  agencyName,
  trigger,
  triggerRef,
}: NewBrandDialogProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState("")
  const [locale, setLocale] = useState("fr")
  const [email, setEmail] = useState("")
  /**
   * Mid-submit guard for `onOpenChange` — same pattern as OKS-12. Mirrors
   * `processing` from the render-prop into a ref every render so ESC +
   * overlay-click can read the latest value outside the Form scope.
   */
  const submittingRef = useRef(false)

  function handleEmailBlur(event: FocusEvent<HTMLInputElement>) {
    const next = event.target.value.trim().toLowerCase()
    if (next !== email) setEmail(next)
  }

  function handleClose() {
    setOpen(false)
    setName("")
    setLocale("fr")
    setEmail("")
    triggerRef?.current?.focus()
  }

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && submittingRef.current) return
        if (!next) handleClose()
        else setOpen(true)
      }}
    >
      {/*
       * Radix Slot lets the child Button's `data-testid` win — don't set
       * one on the trigger wrapper. The parent page is responsible for
       * putting `data-testid="agency-new-brand-trigger"` on the inner
       * Button.
       */}
      <ResponsiveDialogTrigger asChild>{trigger}</ResponsiveDialogTrigger>
      <ResponsiveDialogContent
        className="sm:max-w-md"
        data-testid="agency-new-brand-dialog"
      >
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>
            {t("agency.brands.dialog.new_brand.title")}
          </ResponsiveDialogTitle>
          {/* sr-only meaningful description per Radix a11y rule. */}
          <ResponsiveDialogDescription className="sr-only">
            {t("agency.brands.dialog.new_brand.description")}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        {/* OKS-120 — Form acts as scrollable body in mobile Sheet mode. */}
        <Form
          action={agencyInvitationsPath()}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={handleClose}
          resetOnSuccess
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-0"
        >
          {({ processing, errors }) => {
            submittingRef.current = processing
            return (
              <>
                {/* Path discriminator — backend's single agency endpoint. */}
                <input type="hidden" name="path" value="brand" />

                {/*
                 * Attribution hint — explicit "this brand will be
                 * auto-attached to {{agency}}" so the agency admin
                 * understands the implicit ownership before submit. UX
                 * Spec: render above field group, muted-foreground tone.
                 */}
                <p
                  className="text-muted-foreground text-sm"
                  data-testid="agency-new-brand-attribution-hint"
                >
                  {t("agency.brands.dialog.new_brand.attribution_hint", {
                    agency: agencyName,
                  })}
                </p>

                {errors.base && (
                  <div
                    role="alert"
                    className="border-destructive/40 bg-destructive/10 text-foreground rounded-md border px-3 py-2 text-sm"
                    data-testid="agency-new-brand-error-banner"
                  >
                    {Array.isArray(errors.base)
                      ? errors.base.join(" ")
                      : errors.base}
                  </div>
                )}
                {!online && (
                  <div
                    className="border-muted-foreground/20 bg-muted/40 text-muted-foreground rounded-md border px-3 py-2 text-sm"
                    data-testid="agency-new-brand-offline-banner"
                  >
                    {t("agency.brands.dialog.offline_banner")}
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <Label htmlFor="agency-new-brand-name">
                    {t("agency.brands.dialog.new_brand.name_label")}
                    <span
                      className="text-destructive ml-0.5"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </Label>
                  <Input
                    id="agency-new-brand-name"
                    name="name"
                    type="text"
                    autoComplete="off"
                    autoFocus
                    aria-required="true"
                    value={name}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setName(e.target.value)
                    }
                    placeholder={t(
                      "agency.brands.dialog.new_brand.name_placeholder",
                    )}
                    aria-invalid={errors.name ? "true" : undefined}
                    aria-describedby={
                      errors.name ? "agency-new-brand-name-error" : undefined
                    }
                    disabled={!online || processing}
                  />
                  <InputError
                    messages={errors.name}
                    id="agency-new-brand-name-error"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="agency-new-brand-locale">
                    {t("agency.brands.dialog.new_brand.locale_label")}
                  </Label>
                  <Select
                    items={{
                      fr: t("agency.brands.dialog.locale.fr"),
                      en: t("agency.brands.dialog.locale.en"),
                    }}
                    name="locale"
                    value={locale}
                    onValueChange={(value) => setLocale(value ?? "")}
                    disabled={!online || processing}
                  >
                    <SelectTrigger
                      id="agency-new-brand-locale"
                      aria-invalid={errors.locale ? "true" : undefined}
                      aria-describedby={
                        errors.locale
                          ? "agency-new-brand-locale-error"
                          : undefined
                      }
                      data-testid="agency-new-brand-locale"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fr">
                        {t("agency.brands.dialog.locale.fr")}
                      </SelectItem>
                      <SelectItem value="en">
                        {t("agency.brands.dialog.locale.en")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <InputError
                    messages={errors.locale}
                    id="agency-new-brand-locale-error"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="agency-new-brand-admin-email">
                    {t("agency.brands.dialog.new_brand.email_label")}
                    <span
                      className="text-destructive ml-0.5"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </Label>
                  <Input
                    id="agency-new-brand-admin-email"
                    name="admin_email"
                    type="email"
                    autoComplete="off"
                    aria-required="true"
                    value={email}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setEmail(e.target.value)
                    }
                    onBlur={handleEmailBlur}
                    placeholder={t(
                      "agency.brands.dialog.new_brand.email_placeholder",
                    )}
                    aria-invalid={errors.admin_email ? "true" : undefined}
                    aria-describedby={
                      errors.admin_email
                        ? "agency-new-brand-email-error"
                        : undefined
                    }
                    disabled={!online || processing}
                  />
                  <InputError
                    messages={errors.admin_email}
                    id="agency-new-brand-email-error"
                  />
                </div>

                <ResponsiveDialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={processing}
                    aria-disabled={processing ? "true" : undefined}
                  >
                    {t("agency.brands.dialog.new_brand.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={processing || !online}
                    aria-disabled={processing || !online ? "true" : undefined}
                    data-testid="agency-new-brand-submit"
                  >
                    {processing && (
                      <Loader2
                        className="size-4 animate-spin"
                        aria-hidden="true"
                      />
                    )}
                    {processing
                      ? t("agency.brands.dialog.new_brand.creating")
                      : t("agency.brands.dialog.new_brand.submit")}
                  </Button>
                </ResponsiveDialogFooter>
              </>
            )
          }}
        </Form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
